// What the renderer needs of a painted board, ready to upload (docs/186, docs/186 EP): the ground and the lit
// pixels cut into chunks (each cropped to its opaque pixels: a crawl's chunks are half void), the raised wall
// squares packed into atlases, and, for a night or indoor set, the lit pixels of those squares packed into
// atlases of the same layout (docs/164 ET9). Pure and Phaser-free: the board painter's worker runs it after
// `paintBoard`, so the main thread only uploads. The painted picture itself is not kept: once cut, nothing
// on the board reads it.
import { LIT_COLOURS } from './paint';
import type { PaintedBoard } from './types';

/** A painted board without its pictures (they live in the chunks). */
export type ShownBoard = Omit<PaintedBoard, 'ground' | 'lit'>;
/**
 * A `w`×`h` RGBA rectangle of the board at (x, y) board px, ready for the GPU (`DataTexture` `raw`): rows
 * bottom-up (as Phaser's frames expect every upload) and alpha premultiplied, so the upload needs no
 * conversion. WebGL's own flip-and-premultiply ran on the main thread at ~14 ms a 512 px chunk here.
 */
export interface ChunkData { x: number; y: number; w: number; h: number; data: Uint8ClampedArray }
/** An atlas, ready for the GPU as a chunk is, of one region's pieces. */
export interface Sheet { w: number; h: number; data: Uint8ClampedArray; region: number }
/** Where a raised wall square sits in the wall atlases, and its lit pixels in the lit ones, if it has any. */
export interface Placement { sheet: number; x: number; y: number; lit?: { sheet: number; x: number; y: number } }
/**
 * The standing pieces whose square lies in one chunk-sized part of the board (docs/186, performance): the renderer
 * makes a region's images and uploads its atlases only when the region first comes into view (or in idle
 * frames). `x0…y1` bound its images (they rise above and spill beside the part).
 */
export interface Region { x0: number; y0: number; x1: number; y1: number; items: number[] }

export interface Prepared {
  board: ShownBoard;
  ground: ChunkData[];
  lit: ChunkData[];
  regions: Region[];
  /** The wall atlases, each holding one region's walls (`region`). */
  sheets: Sheet[];
  /** The lit atlases: only the walls with lit pixels, one region each. */
  litSheets: Sheet[];
  /** Per standing index: its place in the wall atlases (its own pixels), undefined for a set's frame. */
  place: Array<Placement | undefined>;
  paintMs: number;
  prepMs: number;
}

/** The side of a wall atlas (px). */
export const ATLAS = 2048;

/** Whether the set keeps lit pixels bright at night (as the painter's `lit` picture). */
export const keepsLit = (set: { time?: string; indoor?: boolean }): boolean => set.time === 'night' || !!set.indoor;

/** The pixel at `i` (RGBA index) is one of the lit colours. */
export function isLit(px: ArrayLike<number>, i: number): boolean {
  // every lit colour is bright red (≥ 0xf2): most pixels are out at the first test
  if (px[i]! < 0xf2 || !px[i + 3]) return false;
  for (const c of LIT_COLOURS) if (c[0] === px[i] && c[1] === px[i + 1] && c[2] === px[i + 2]) return true;
  return false;
}

/** Only the lit pixels of `px` (opaque), or undefined when it has none. */
export function litOnly(px: Uint8ClampedArray): Uint8ClampedArray | undefined {
  let out: Uint8ClampedArray | undefined;
  for (let i = 0; i < px.length; i += 4) {
    if (!isLit(px, i)) continue;
    out ??= new Uint8ClampedArray(px.length);
    out[i] = px[i]!; out[i + 1] = px[i + 1]!; out[i + 2] = px[i + 2]!; out[i + 3] = 255;
  }
  return out;
}

/** In place: rows of a `w`×`h` RGBA picture turned bottom-up, alpha premultiplied (what `DataTexture` `raw` uploads). */
export function toGl(px: Uint8ClampedArray, w: number, h: number): Uint8ClampedArray {
  premultiply(px);
  const row = w * 4, tmp = new Uint8ClampedArray(row);
  for (let a = 0, b = h - 1; a < b; a++, b--) {
    tmp.set(px.subarray(a * row, (a + 1) * row));
    px.copyWithin(a * row, b * row, (b + 1) * row);
    px.set(tmp, b * row);
  }
  return px;
}

function premultiply(px: Uint8ClampedArray): void {
  for (let i = 3; i < px.length; i += 4) {
    const a = px[i]!;
    if (a === 255) continue;
    if (a === 0) { px[i - 3] = 0; px[i - 2] = 0; px[i - 1] = 0; continue; }
    px[i - 3] = Math.round((px[i - 3]! * a) / 255); px[i - 2] = Math.round((px[i - 2]! * a) / 255); px[i - 1] = Math.round((px[i - 1]! * a) / 255);
  }
}

/** `px` (W×H RGBA) cut into `size` px chunks, each cropped to its opaque pixels and made ready for the GPU (`toGl`); empty chunks left out. */
export function cutChunks(px: Uint8ClampedArray, W: number, H: number, size: number): ChunkData[] {
  const out: ChunkData[] = [];
  for (let y = 0; y < H; y += size) for (let x = 0; x < W; x += size) {
    const cw = Math.min(size, W - x), ch = Math.min(size, H - y);
    let x0 = cw, x1 = -1, y0 = ch, y1 = -1;
    for (let r = 0; r < ch; r++) {
      const o = ((y + r) * W + x) * 4;
      let a = -1, b = -1;
      for (let i = 0; i < cw; i++) if (px[o + i * 4 + 3]) { a = i; break; }
      if (a < 0) continue;
      for (let i = cw - 1; i >= a; i--) if (px[o + i * 4 + 3]) { b = i; break; }
      if (a < x0) x0 = a;
      if (b > x1) x1 = b;
      if (r < y0) y0 = r;
      y1 = r;
    }
    if (x1 < 0) continue;
    const w = x1 - x0 + 1, h = y1 - y0 + 1;
    const data = new Uint8ClampedArray(w * h * 4);
    for (let r = 0; r < h; r++) {
      const o = ((y + y0 + r) * W + x + x0) * 4;
      data.set(px.subarray(o, o + w * 4), (h - 1 - r) * w * 4);
    }
    premultiply(data);
    out.push({ x: x + x0, y: y + y0, w, h, data });
  }
  return out;
}

/** Cuts and packs a painted board for the renderer (`chunk`: the ground chunks' side in px). */
export function prepareBoard(painted: PaintedBoard, set: { time?: string; indoor?: boolean }, chunk: number, paintMs = 0): Prepared {
  const t0 = performance.now();
  const { ground, lit: litPx, ...board } = painted;
  const W = painted.w * painted.cell, H = painted.h * painted.cell;
  const groundChunks = cutChunks(ground, W, H, chunk);
  const litChunks = litPx ? cutChunks(litPx, W, H, chunk) : [];
  // the standing pieces by the part of the board their square is in
  const nx = Math.max(1, Math.ceil(W / chunk)), ny = Math.max(1, Math.ceil(H / chunk));
  const regions: Region[] = Array.from({ length: nx * ny }, () => ({ x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity, items: [] }));
  painted.standing.forEach((s, i) => {
    const rx = Math.min(nx - 1, Math.max(0, Math.floor(((s.at.x + 0.5) * painted.cell) / chunk)));
    const ry = Math.min(ny - 1, Math.max(0, Math.floor(((s.at.y + 0.5) * painted.cell) / chunk)));
    const r = regions[ry * nx + rx]!;
    r.items.push(i);
    r.x0 = Math.min(r.x0, s.x); r.y0 = Math.min(r.y0, s.y); r.x1 = Math.max(r.x1, s.x + s.w); r.y1 = Math.max(r.y1, s.y + s.h);
  });
  // each region's own pixels (raised wall squares) shelf-packed into its atlases, and the lit pixels of
  // those that have any into its lit atlases
  const place: Array<Placement | undefined> = new Array(painted.standing.length).fill(undefined);
  const sheets: Sheet[] = [], litSheets: Sheet[] = [];
  const keep = keepsLit(set);
  regions.forEach((r, ri) => {
    const own = r.items.filter((i) => painted.standing[i]!.px);
    if (!own.length) return;
    const at = packInto(own.map((i) => ({ i, w: painted.standing[i]!.w, h: painted.standing[i]!.h, px: painted.standing[i]!.px! })), ri, sheets);
    for (const [i, p] of at) place[i] = p;
    if (!keep) return;
    const lit = own.flatMap((i) => { const s = painted.standing[i]!, px = litOnly(s.px!); return px ? [{ i, w: s.w, h: s.h, px }] : []; });
    if (!lit.length) return;
    for (const [i, p] of packInto(lit, ri, litSheets)) place[i]!.lit = p;
  });
  for (const sh of [...sheets, ...litSheets]) toGl(sh.data, sh.w, sh.h);
  return { board, ground: groundChunks, lit: litChunks, regions, sheets, litSheets, place, paintMs, prepMs: performance.now() - t0 };
}

/** Shelf-packs pictures into new sheets of region `region` (appended to `out`): where each went. */
function packInto(list: Array<{ i: number; w: number; h: number; px: Uint8ClampedArray }>, region: number, out: Sheet[]): Map<number, { sheet: number; x: number; y: number }> {
  const width = Math.min(ATLAS, Math.max(512, ...list.map((p) => p.w)));
  const at = new Map<number, { sheet: number; x: number; y: number }>();
  const heights: number[] = [];
  const first = out.length;
  let sx = 0, sy = 0, shelf = 0;
  for (const p of list) {
    if (sx + p.w > width) { sx = 0; sy += shelf; shelf = 0; }
    if (sy + p.h > ATLAS || !heights.length) { heights.push(0); sx = 0; sy = 0; shelf = 0; }
    at.set(p.i, { sheet: first + heights.length - 1, x: sx, y: sy });
    sx += p.w; shelf = Math.max(shelf, p.h);
    heights[heights.length - 1] = Math.max(heights[heights.length - 1]!, sy + shelf);
  }
  for (const h of heights) out.push({ w: width, h: Math.max(1, h), data: new Uint8ClampedArray(width * Math.max(1, h) * 4), region });
  for (const p of list) {
    const a = at.get(p.i)!, sh = out[a.sheet]!;
    for (let r = 0; r < p.h; r++) sh.data.set(p.px.subarray(r * p.w * 4, (r + 1) * p.w * 4), ((a.y + r) * sh.w + a.x) * 4);
  }
  return at;
}

/**
 * The buffers a `Prepared` can hand over to another thread without copying (each once): the ones made
 * here. A standing piece's own pixels are copied, as they may be a view of the set's (which the worker keeps).
 */
export function transfersOf(p: Prepared): ArrayBuffer[] {
  const out = new Set<ArrayBuffer>();
  const add = (a: ArrayBufferView | undefined) => { if (a && a.buffer instanceof ArrayBuffer) out.add(a.buffer); };
  for (const c of [...p.ground, ...p.lit]) add(c.data);
  for (const s of [...p.sheets, ...p.litSheets]) add(s.data);
  return [...out];
}
